import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {first, map, catchError} from 'rxjs/operators';

import {Store} from '@ngrx/store';
import * as fromTrafficSources from '../_store/traffic-sources.reducer';
import * as TrafficSourcesActions from '../_store/traffic-sources.actions';
import {FormHelper} from '../../../../_common';
import {ApiService} from '../../../../shared/_services/api.service';
import {SharedService} from '../../../../shared/_services/shared.service';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import { Observable, forkJoin, of } from 'rxjs';
import { TrafficSource, Placement } from '../../../../shared/_models/models';

@Injectable({providedIn: 'root'})
export class SupplyTrafficSourceService {

  constructor(
    private http: HttpClient,
    private fh: FormHelper,
    private store: Store<fromTrafficSources.TrafficSourcesState>,
    private api: ApiService,
    private router: Router,
    private sharedService: SharedService,
    private notification: NzNotificationService
  ) {
  }

  fetchTrafficSources() {
    this.store.dispatch(new TrafficSourcesActions.FetchTrafficSourcesRequestAction());
    this.api.getTrafficSources()
      .pipe(
        map((res: any) => {
          return res.reduce((result, val) => {
            const newVal = val;
            newVal.tag = !val.deletedAt ? val.status : 'DELETED';
            result.push(newVal);
            return result;
          }, []);
        })
      )
      .subscribe((mutatedRes: any) => {
          this.store.dispatch(new TrafficSourcesActions.FetchTrafficSourcesSuccessAction({trafficSources: mutatedRes}));
        },
        (err: any) => {
          this.store.dispatch(new TrafficSourcesActions.FetchTrafficSourcesFailureAction(err));
        });
  }

  fetchTrafficSourceById(id: number | string) {
    this.store.dispatch(new TrafficSourcesActions.FetchTrafficSourceByIdRequestAction());
    this.api.getTrafficSourceById(id)
      .pipe(first())
      .subscribe((res: any) => {
        this.store.dispatch(new TrafficSourcesActions.FetchTrafficSourceByIdSuccessAction({editedTrafficSource: res}));
      }, (err: any) => {
        this.store.dispatch(new TrafficSourcesActions.FetchTrafficSourceByIdFailureAction(err));
        const errorMessage = err?.error?.message || 'Traffic source not found';
        this.notification.error('Error', errorMessage);
        this.router.navigate(['/supply/traffic-sources']);
      });
  }
  fetchSsps(search: any = {}) {
    this.store.dispatch(new TrafficSourcesActions.FetchSspsRequestAction());
    this.api.getSspBasic()
      .subscribe((res: any) => {
        this.store.dispatch(new TrafficSourcesActions.FetchSspsSuccessAction({ssps: res}));
      }, (err: any) => {
        this.store.dispatch(new TrafficSourcesActions.FetchSspsFailureAction(err));
      });
  }

  getAvailablePlacements(searchValue: string = '', currentTrafficSourceId?: number): Observable<Placement[]> {
    const query = this.sharedService.buildPlacementSearchQuery(searchValue);
    query.sorting.sortingField = 'placementId';

    return forkJoin({
      placements: this.api.getPlacements(query),
      trafficSources: this.store.select(fromTrafficSources.getTrafficSources).pipe(first())
    }).pipe(
      map(({ placements, trafficSources }) =>
        this.filterAvailablePlacements(placements.data, trafficSources, currentTrafficSourceId)
      ),
      catchError((err: any) => {
        this.notification.error('Error', 'Failed to load available placements.');
        return of([]);
      })
    );
  }

  private filterAvailablePlacements(
    placements: Placement[],
    trafficSources: TrafficSource[],
    currentTrafficSourceId?: number
  ): Placement[] {
    const usedPlacementIds = trafficSources
      .filter(ts => ts.trafficSourceId !== currentTrafficSourceId && ts.defaultPlacementId)
      .map(ts => ts.defaultPlacementId);
    return placements.filter(
      placement => !usedPlacementIds.includes(placement.placementId)
    );
  }


  createTrafficSource(trafficSource): any {
    this.store.dispatch(new TrafficSourcesActions.CreateTrafficSourceRequestAction());
    this.api.createTrafficSource(trafficSource)
      .subscribe((res: any) => {
        res.tag = !res.deletedAt ? res.status : 'deleted';
        this.store.dispatch(new TrafficSourcesActions.CreateTrafficSourceSuccessAction({editedTrafficSource: res}));
        this.sharedService.onHttpCompletionResolve(true, 'Success',  'Traffic source created',
          '/supply/traffic-sources', 'traffic-source-created');
      }, (err: any) => {
        this.store.dispatch(new TrafficSourcesActions.CreateTrafficSourceFailureAction(err));
        this.notification.error('Error', 'Something went wrong. Traffic source was not created.');
      });
  }

  editTrafficSource(trafficSource: any, id: number | string): any {
    this.store.dispatch(new TrafficSourcesActions.EditTrafficSourceRequestAction());
    this.api.editTrafficSource(trafficSource, id)
      .subscribe((res: any) => {
        res.tag = !res.deletedAt ? res.status : 'DELETED';
        this.store.dispatch(new TrafficSourcesActions.EditTrafficSourceSuccessAction({editedTrafficSource: res}));
        this.sharedService.onHttpCompletionResolve(true, 'Success',  'Traffic source updated',
          '/supply/traffic-sources', 'traffic-source-updated');
      }, (err: any) => {
        this.store.dispatch(new TrafficSourcesActions.EditTrafficSourceFailureAction(err));
        this.notification.error('Error', 'Something went wrong. Traffic source was not updated.');
      });
  }

  deleteTrafficSource(id: number | string): any {
    this.store.dispatch(new TrafficSourcesActions.DeleteTrafficSourceRequestAction());
    this.api.deleteTrafficSource(id)
      .subscribe(() => {
        this.store.dispatch(new TrafficSourcesActions.DeleteTrafficSourceSuccessAction({deleteTrafficSourceId: id}));
      }, (err: any) => {
        this.store.dispatch(new TrafficSourcesActions.DeleteTrafficSourceFailureAction(err));
      });
  }

  getAll(): Observable<TrafficSource[]> {
    return this.api.getTrafficSources();
  }
}
